import { Currency } from "@redotech/money/currencies";
import { ReturnTotalCalculationsOutputSchema } from "@redotech/redo-return-operations-common/draft-return/draft-return";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/** A `{name, value}` option pair shown under a review line item. */
export const PosReviewVariantSchema = z.object({
  name: z.string(),
  value: z.string(),
});

/** A neutral badge next to a review line item (color is POS-irrelevant). */
export const PosReviewItemBadgeSchema = z.object({ text: z.string() });

/** One review line item, carrying the raw fields the client formatter reads. */
export const PosReviewItemSchema = z.object({
  id: z.string(),
  pendingReturnItemId: z.string(),
  imageSrc: z.string(),
  title: z.string(),
  subtitle: z.string().nullable(),
  quantity: z.number(),
  variants: z.array(PosReviewVariantSchema),
  formattedPrice: z.string(),
  isSelected: z.boolean(),
  badgeText: z.string().nullable(),
  currency: createEnum(Currency),
  price: z.string(),
  itemValue: z.string().nullable(),
  returnTypeBadges: z.array(PosReviewItemBadgeSchema),
  isTnplContractLineItem: z.boolean(),
  presentmentCurrency: z.string().nullable(),
  exchangeRate: z.number(),
  variantId: z.string().nullable(),
  collections: z.array(z.string()),
});

/** A single labeled money line in the review summary card. */
export const PosReviewSubtotalSchema = z.object({
  title: z.string(),
  total: z.number(),
});

/** The review summary-card lines (the subset the POS SummaryCard renders). */
export const PosReviewSummarySchema = z.object({
  adjustmentValue: z.number(),
  adjustmentValueString: z.string(),
  currency: createEnum(Currency),
  feesHeader: z.string(),
  feesOrderHeader: z.string(),
  productSubtotals: z.array(PosReviewSubtotalSchema),
  taxSubtotals: z.array(PosReviewSubtotalSchema),
  newOrderItemsSubtotals: z.array(PosReviewSubtotalSchema),
  newVariantExchangeItemSubtotals: z.array(PosReviewSubtotalSchema),
  newOrderTaxes: z.array(PosReviewSubtotalSchema),
  returnValue: z.number(),
  newOrderValue: z.number(),
  totalRefund: z.number(),
  totalStoreCredit: z.number(),
  totalPaymentOwed: z.number(),
});

/** Display-ready POS review state; raw `calculations` pass through unchanged. */
export const PosReviewViewSchema = z.object({
  items: z.object({
    greenReturns: z.array(PosReviewItemSchema),
    nonGreenReturns: z.array(PosReviewItemSchema),
    compensationExchangeItems: z.array(PosReviewItemSchema),
    newOrderItems: z.array(PosReviewItemSchema),
  }),
  texts: z.object({
    oldItemsHeader: z.string(),
    newItemsHeader: z.string(),
    needsEmailText: z.string(),
    submitReturn: z.string(),
  }),
  summary: PosReviewSummarySchema,
  email: z.object({
    needsEmail: z.boolean(),
    shouldShowEmailInput: z.boolean(),
  }),
  calculations: ReturnTotalCalculationsOutputSchema,
});

export type PosReviewView = z.infer<typeof PosReviewViewSchema>;
export type PosReviewItem = z.infer<typeof PosReviewItemSchema>;
export type PosReviewSummary = z.infer<typeof PosReviewSummarySchema>;
export type PosReviewSubtotal = z.infer<typeof PosReviewSubtotalSchema>;

export const getPosReviewView = {
  name: "getPosReviewView" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    usePresentmentCurrency: z.boolean().optional(),
  }),
  output: z.object({ view: PosReviewViewSchema }),
};
